import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const QRScanner = ({ onScanResult }) => {
  const [manualInput, setManualInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [location, setLocation] = useState('Main Entry');
  const [notes, setNotes] = useState('');
  const [todayStats, setTodayStats] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchTodayStats();
    const interval = setInterval(fetchTodayStats, 30000); // Refresh every 30s
    return () => {
      clearInterval(interval);
      stopCamera();
    };
  }, []);

  const fetchTodayStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId') || 'sevenwonders';
      
      const response = await axios.post(`${API_BASE_URL}/api/ticket-verification/bulk-status`, {
        date: new Date().toISOString().split('T')[0],
        tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTodayStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleScan = async (action) => {
    if (!manualInput.trim()) return;
    
    setScanning(true);
    try {
      const token = localStorage.getItem('token');
      const staffId = localStorage.getItem('staffId') || localStorage.getItem('userId');
      
      const response = await axios.post(`${API_BASE_URL}/api/ticket-verification/${action}`, {
        qrData: manualInput,
        staffId,
        location,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onScanResult(response.data);
      setManualInput('');
      setNotes('');
      fetchTodayStats(); // Refresh stats
    } catch (error) {
      onScanResult({
        success: false,
        message: error.response?.data?.message || 'Verification failed'
      });
    } finally {
      setScanning(false);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
      setCameraActive(true);
      startScanning();
    } catch (error) {
      console.error('Camera access denied:', error);
      alert('Camera access is required for QR scanning. Please allow camera permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const startScanning = () => {
    const scanInterval = setInterval(() => {
      if (videoRef.current && canvasRef.current && cameraActive) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const context = canvas.getContext('2d');
        
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = window.jsQR && window.jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          setManualInput(code.data);
          stopCamera();
          clearInterval(scanInterval);
        }
      }
    }, 100);
    
    setTimeout(() => clearInterval(scanInterval), 30000); // Stop after 30 seconds
  };

  const generateSampleQR = () => {
    const sampleQR = JSON.stringify({
      bookingId: 'BK' + Date.now(),
      tenantId: 'sevenwonders',
      ticketType: 'Entry Ticket',
      visitDate: new Date().toISOString(),
      customerName: 'Test Customer'
    });
    setManualInput(sampleQR);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      borderRadius: '20px',
      padding: '24px',
      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
      border: '1px solid rgba(226, 232, 240, 0.8)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '800',
          color: '#1a202c',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          margin: 0
        }}>
          📱 Ticket Scanner
        </h3>
        <button
          onClick={generateSampleQR}
          style={{
            padding: '6px 12px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          📝 Sample QR
        </button>
      </div>
      
      {/* Today's Stats */}
      {todayStats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
          gap: '12px',
          marginBottom: '20px',
          padding: '16px',
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          {[
            { label: 'Total', value: todayStats.total, color: '#3b82f6' },
            { label: 'In', value: todayStats.checkedIn, color: '#10b981' },
            { label: 'Out', value: todayStats.checkedOut, color: '#f59e0b' },
            { label: 'Inside', value: todayStats.inside, color: '#8b5cf6' }
          ].map((stat, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '800', color: stat.color }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Location & Notes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div>
          <label style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#4a5568',
            display: 'block',
            marginBottom: '6px'
          }}>📍 Location:</label>
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'white'
            }}
          >
            <option value="Main Entry">Main Entry</option>
            <option value="Side Gate">Side Gate</option>
            <option value="VIP Entrance">VIP Entrance</option>
            <option value="Group Entry">Group Entry</option>
          </select>
        </div>
        <div>
          <label style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#4a5568',
            display: 'block',
            marginBottom: '6px'
          }}>📝 Notes:</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        </div>
      </div>
      
      {/* Camera Scanner */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#4a5568'
          }}>
            📷 QR Scanner:
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={cameraActive ? stopCamera : startCamera}
              style={{
                padding: '6px 12px',
                background: cameraActive ? 
                  'linear-gradient(135deg, #ef4444, #dc2626)' : 
                  'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {cameraActive ? '📷 Stop Camera' : '📷 Start Camera'}
            </button>
            <button
              onClick={generateSampleQR}
              style={{
                padding: '6px 12px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              📝 Sample QR
            </button>
          </div>
        </div>
        
        {cameraActive && (
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '400px',
            margin: '0 auto 16px',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '3px solid #10b981'
          }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
              playsInline
              muted
            />
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200px',
              height: '200px',
              border: '2px solid #10b981',
              borderRadius: '12px',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              📱 Point camera at QR code
            </div>
          </div>
        )}
        
        <textarea
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder="QR code data will appear here or paste manually..."
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            fontSize: '14px',
            minHeight: '80px',
            resize: 'vertical',
            fontFamily: 'monospace',
            background: 'rgba(248, 250, 252, 0.8)'
          }}
        />
      </div>
      
      {/* Action Buttons */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px'
      }}>
        <button
          onClick={() => handleScan('check-in')}
          disabled={!manualInput.trim() || scanning}
          style={{
            padding: '14px 20px',
            background: manualInput.trim() && !scanning ? 
              'linear-gradient(135deg, #10b981, #059669)' : '#cbd5e1',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: manualInput.trim() && !scanning ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {scanning ? '⏳ Processing...' : '✅ Check In'}
        </button>
        
        <button
          onClick={() => handleScan('check-out')}
          disabled={!manualInput.trim() || scanning}
          style={{
            padding: '14px 20px',
            background: manualInput.trim() && !scanning ? 
              'linear-gradient(135deg, #f59e0b, #d97706)' : '#cbd5e1',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: manualInput.trim() && !scanning ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {scanning ? '⏳ Processing...' : '🚪 Check Out'}
        </button>
      </div>
    </div>
  );
};

export default QRScanner;